import { IdentificationMode, Step } from '../../constants';

/**
 * @typedef {import('../../constants').Step} Step
 */

/**
 * @typedef {import('../../constants').IdentificationMode} IdentificationMode
 */

/**
 * Navigation configuration for a single step.
 *
 * @typedef {Object} NavigationConfig
 * @property {{step: Step, mode?: IdentificationMode}} [next] next step configuration
 * @property {{step: Step, mode?: IdentificationMode}} [back] previous step configuration
 * @property {{step: Step, mode?: IdentificationMode}[]} [named] array of supported named steps that can be navigated from the current step
 */

/**
 * @typedef {Object.<string, NavigationConfig?>} StepNavigationMap
 */

/**
 * @param {Object} options
 * @param {boolean} options.skipActivation
 * @returns {StepNavigationMap}
 */
const signupModeMap = ({ skipActivation }) => ({
    [Step.EMAIL_IDENTIFICATION]: {
        next: {
            step: Step.PROFILE_SETUP
        },
        back: {
            step: Step.INITIAL,
            mode: IdentificationMode.UNKNOWN
        }
    },
    [Step.PROFILE_SETUP]: {
        next: skipActivation ? undefined : { step: Step.ACTIVATION },
        back: {
            step: Step.EMAIL_IDENTIFICATION,
            mode: IdentificationMode.UNKNOWN
        }
    },
    [Step.ACTIVATION]: skipActivation ? undefined : {
        named: [{
            step: Step.CHANGE_EMAIL
        }]
    },
    [Step.CHANGE_EMAIL]: skipActivation ? undefined : {
        next: {
            step: Step.ACTIVATION
        },
        back: {
            step: Step.ACTIVATION
        }
    }
});

/**
 * @returns {StepNavigationMap}
 */
const activationModeMap = () => ({
    [Step.ACTIVATION]: {
        named: [{ step: Step.CHANGE_EMAIL }]
    },
    [Step.CHANGE_EMAIL]: {
        named: [{ step: Step.ACTIVATION }],
        next: {
            step: Step.ACTIVATION
        },
        back: {
            step: Step.ACTIVATION
        }
    }
});

/**
 * @returns {StepNavigationMap}
 */
const signinModeMap = () => ({
    [Step.EMAIL_IDENTIFICATION]: {
        back: {
            step: Step.INITIAL,
            mode: IdentificationMode.UNKNOWN
        },
        named: [{ step: Step.RESET_PASSWORD }]
    },
    [Step.RESET_PASSWORD]: {
        back: {
            step: Step.EMAIL_IDENTIFICATION
        }
    }
});

/**
 * @returns {StepNavigationMap}
 */
const socialSigninModeMap = () => ({});

/**
 * @returns {StepNavigationMap}
 */
const socialSignupModeMap = () => ({
    [Step.INITIAL]: {
        next: {
            step: Step.PROFILE_SETUP
        }
    },
    [Step.SOCIAL_PROVIDER_FALLBACK]: {
        next: {
            step: Step.PROFILE_SETUP
        },
        named: [{
            step: Step.INITIAL,
            mode: IdentificationMode.UNKNOWN
        }]
    },
    [Step.PROFILE_SETUP]: {
        back: {
            step: Step.INITIAL,
            mode: IdentificationMode.UNKNOWN
        }
    }
});

/**
 * @returns {StepNavigationMap}
 */
const googleOneTapModeMap = () => ({
    [Step.SOCIAL_PROVIDER_FALLBACK]: {
        next: {
            step: Step.PROFILE_SETUP
        }
    }
});

/**
 * @returns {StepNavigationMap}
 */
const unknownModeMap = () => ({
    [Step.INITIAL]: {
        next: {
            step: Step.EMAIL_IDENTIFICATION
        },
        named: [{ step: Step.SOCIAL_PROVIDER_FALLBACK }]
    },
    [Step.SOCIAL_PROVIDER_FALLBACK]: {
        next: {
            step: Step.PROFILE_SETUP
        },
        named: [{
            step: Step.INITIAL,
            mode: IdentificationMode.UNKNOWN
        }]
    },
    [Step.EMAIL_IDENTIFICATION]: {
        back: {
            step: Step.INITIAL
        },
        named: [{ step: Step.RESET_PASSWORD }]
    },
    [Step.RESET_PASSWORD]: {
        back: {
            step: Step.EMAIL_IDENTIFICATION
        }
    }
});

/**
 * Get the step navigation map for the current state
 * @param {Object} state
 * @param {IdentificationMode} state.mode current identification mode
 * @param {boolean} state.skipActivation whether to skip activation step
 * @returns {StepNavigationMap}
 */
export const getStepNavigationMap = ({
    mode,
    skipActivation = false
}) => {
    switch (mode) {
        case IdentificationMode.UNKNOWN:
            return unknownModeMap();
        case IdentificationMode.SIGNUP:
            return signupModeMap({ skipActivation });
        case IdentificationMode.SIGNIN:
            return signinModeMap();
        case IdentificationMode.SOCIAL_SIGNIN:
            return socialSigninModeMap();
        case IdentificationMode.SOCIAL_SIGNUP:
            return socialSignupModeMap();
        case IdentificationMode.GOOGLE_ONE_TAP:
            return googleOneTapModeMap();
        case IdentificationMode.ACTIVATION:
            return activationModeMap();
        default:
            return {};
    }
};
